import React from 'react';
import {
  Box,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import { SignatureFlow, SignatureFlowStep } from '../types';

type ChipColor = 'default' | 'success' | 'warning' | 'error' | 'info';

const DETAIL_ORDER = [
  'smtpRequestId',
  'email',
  'language',
  'to',
  'subject',
  'contentSource',
  'selectedContentLength',
  'signatureLength',
  'attachmentsCount',
  'pullAttempts',
  'statusCode',
  'cachedVersion',
  'bodyPreview',
  'reason'
];

function getStatusColor(status: SignatureFlowStep['status']): ChipColor {
  switch (status) {
    case 'completed':
      return 'success';
    case 'failed':
      return 'error';
    case 'running':
      return 'info';
    case 'skipped':
      return 'default';
    default:
      return 'warning';
  }
}

function formatTimestamp(value: string | null) {
  if (!value) {
    return 'Waiting';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleString();
}

function formatValue(value: any) {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }

  if (Array.isArray(value)) {
    return value.join(', ');
  }

  if (typeof value === 'object') {
    return JSON.stringify(value);
  }

  return String(value);
}

function getDetailEntries(step: SignatureFlowStep) {
  const details = step.details || {};
  const ordered = DETAIL_ORDER
    .filter((key) => key in details)
    .map((key) => [key, details[key]] as const);
  const remaining = Object.keys(details)
    .filter((key) => !DETAIL_ORDER.includes(key))
    .sort()
    .map((key) => [key, details[key]] as const);

  return [...ordered, ...remaining].filter(([, value]) => formatValue(value) !== null);
}

function formatLabel(key: string) {
  return key
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (char) => char.toUpperCase());
}

interface SignatureFlowTableProps {
  flow: SignatureFlow;
}

const SignatureFlowTable: React.FC<SignatureFlowTableProps> = ({ flow }) => {
  const steps = Array.isArray(flow.steps) ? [...flow.steps].sort((a, b) => a.order - b.order) : [];

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="h6">Flow verification</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
        SMTP request ID: {flow.smtpRequestId || 'Not assigned'} | Started: {formatTimestamp(flow.startedAt)}
      </Typography>

      <TableContainer sx={{ mt: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 220 }}>Step</TableCell>
              <TableCell sx={{ width: 120 }}>Status</TableCell>
              <TableCell sx={{ width: 190 }}>Last update</TableCell>
              <TableCell>Details</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {steps.map((step) => (
              <TableRow key={step.id} hover>
                <TableCell>
                  <Typography variant="subtitle2">{step.order}. {step.label}</Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={step.status}
                    color={getStatusColor(step.status)}
                    size="small"
                    sx={{ textTransform: 'capitalize' }}
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{formatTimestamp(step.updatedAt)}</Typography>
                </TableCell>
                <TableCell>
                  {getDetailEntries(step).length > 0 ? (
                    <Box sx={{ display: 'grid', gap: 0.5 }}>
                      {getDetailEntries(step).map(([key, rawValue]) => (
                        <Typography key={key} variant="caption" sx={{ display: 'block', wordBreak: 'break-word' }}>
                          <strong>{formatLabel(key)}:</strong> {formatValue(rawValue)}
                        </Typography>
                      ))}
                    </Box>
                  ) : (
                    <Typography variant="caption" color="text.secondary">
                      No details yet.
                    </Typography>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default SignatureFlowTable;
